const PAGE = "c-link_in_bio--page";

// Mobile is an interactive projection of the existing editor, never a second draft.
export default class MobileEditor {
  constructor(editor) {
    this.editor = editor;
    this.query = matchMedia("(max-width: 47.5em)");
    this.changed = async () => {
      this.resetSettingsSlugEditor();
      await this.detailSheet?.close({ restoreFocus: false });
      await this.sheet?.close();
      this.discardPendingSection();
      this.previewing = false;
      editor.element.classList.toggle("lib-editor--mobile", this.query.matches);
      editor.refreshPreview();
      editor.updateControls();
    };
    this.query.addEventListener("change", this.changed);
    editor.element.classList.toggle("lib-editor--mobile", this.query.matches);
  }

  disconnect() {
    this.navigation?.removeAttribute("inert");
    this.detailSheet?.element.close();
    this.detailSheet?.restore();
    this.sheet?.element.close();
    this.sheet?.restore();
    this.discardPendingSection();
    this.query.removeEventListener("change", this.changed);
  }

  get sheet() {
    return this.editor.application.getControllerForElementAndIdentifier(
      this.editor.element.querySelector("#link-in-bio-mobile-sheet"), "link-in-bio-editor-mobile-sheet"
    );
  }

  get detailSheet() {
    return this.editor.application.getControllerForElementAndIdentifier(
      this.editor.element.querySelector("#link-in-bio-mobile-detail-sheet"), "link-in-bio-editor-mobile-sheet"
    );
  }

  get labels() { return this.editor.mobileLabelsValue; }

  activate(source) {
    // Reused controls may still be binding after the first preview render.
    const binding = source.dataset.action.split(" ").find(action => action.startsWith("click->"));
    const [identifier, method] = binding.replace("click->", "").split("#");
    const owner = source.closest(`[data-controller~='${identifier}']`);
    const open = () => {
      if (!owner.isConnected) return;
      const controller = this.editor.application.getControllerForElementAndIdentifier(owner, identifier);
      if (controller) controller[method]({ currentTarget: source });
      else requestAnimationFrame(open);
    };
    open();
  }

  button(label, iconName, action) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "lib-editor__mobile-control";
    button.setAttribute("aria-label", label);
    button.title = label;
    button.dataset.mobileFocus = label;
    const icon = this.editor.element.querySelector(`template[data-mobile-icon='${iconName}']`)?.content.cloneNode(true);
    if (icon) button.append(icon);
    button.addEventListener("click", action);
    return button;
  }

  inlineField(source, name) {
    const wrapper = document.createElement("span");
    wrapper.className = "lib-editor__mobile-inline-field";
    if (!source) return wrapper;
    const field = source.cloneNode(false);
    field.removeAttribute("id");
    field.removeAttribute("name");
    field.removeAttribute("data-editor-field");
    field.className = "lib-editor__mobile-inline";
    field.value = source.value;
    field.placeholder = this.labels.placeholders?.[name] || field.placeholder;
    field.dataset.previewOnly = "true";
    field.dataset.mobileInlineField = name;
    field.setAttribute("aria-label", source.getAttribute("aria-label") || source.labels?.[0]?.textContent.trim() || name);
    field.addEventListener("input", () => {
      source.value = field.value;
      source.dispatchEvent(new CustomEvent("input", { bubbles: true, detail: { mobileInline: true } }));
    });
    if (name === "section_name") {
      field.addEventListener("input", () => this.editor.resizeCategoryName(field));
      field.addEventListener("keydown", (event) => {
        if (event.key === "Enter" && !event.isComposing) {
          event.preventDefault();
          field.blur();
        }
      });
    }
    wrapper.addEventListener("click", (event) => {
      if (event.target === wrapper) field.focus();
    });
    wrapper.append(field);
    return wrapper;
  }

  captureInlineFocus(root) {
    const field = document.activeElement;
    if (!this.query.matches || this.previewing || !root.contains(field) || !field.dataset.mobileInlineField) return;

    return {
      name: field.dataset.mobileInlineField,
      sectionKey: field.closest("[data-link-in-bio-preview-section-id]")?.dataset.linkInBioPreviewSectionId,
      start: field.selectionStart,
      end: field.selectionEnd,
      direction: field.selectionDirection,
    };
  }

  restoreInlineFocus(root, focus) {
    if (!focus || !this.query.matches || this.previewing) return;
    const scope = focus.sectionKey
      ? root.querySelector(`[data-link-in-bio-preview-section-id='${CSS.escape(focus.sectionKey)}']`)
      : root;
    const field = scope?.querySelector(`[data-mobile-inline-field='${CSS.escape(focus.name)}']`);
    if (!field) return;

    field.focus({ preventScroll: true });
    field.setSelectionRange(focus.start, focus.end, focus.direction);
  }

  capturePageScrollPosition() {
    if (!this.query.matches || this.previewing) return;

    return { left: window.scrollX, top: window.scrollY };
  }

  restorePageScrollPosition(position) {
    if (!position || !this.query.matches || this.previewing) return;

    requestAnimationFrame(() => window.scrollTo(position.left, position.top));
  }

  decorate(root) {
    root.inert = !this.query.matches;
    root.querySelectorAll(".lib-editor__mobile-edit").forEach(control => control.remove());
    this.settingsButton = null;
    this.navigation = document.querySelector('[data-controller="navigation"]');
    const previewing = this.query.matches && Boolean(this.previewing);
    this.editor.element.closest(".main-container").style.setProperty("--lib-navigation-height", `${this.navigation?.offsetHeight || 0}px`);
    if (this.navigation) this.navigation.inert = previewing;
    this.editor.element.querySelectorAll(".lib-editor__header-actions > :not(.lib-editor__mobile-preview)").forEach(control => {
      control.inert = previewing;
    });
    this.editor.element.classList.toggle("lib-editor--visitor-preview", Boolean(this.previewing));
    const toggle = this.editor.element.querySelector(".lib-editor__mobile-preview");
    toggle.setAttribute("aria-label", this.previewing ? this.labels.edit : this.labels.preview);
    toggle.title = toggle.getAttribute("aria-label");
    toggle.replaceChildren(this.editor.element.querySelector(`template[data-mobile-icon='${this.previewing ? "edit" : "eye"}']`).content.cloneNode(true));
    if (!this.query.matches) return;
    if (this.previewing) {
      root.querySelectorAll("a").forEach(link => link.removeAttribute("tabindex"));
      return;
    }
    const profile = root.querySelector(`.${PAGE}-profile`);
    const name = root.querySelector(`.${PAGE}-name`);
    name.replaceChildren(this.inlineField(
      this.editor.element.querySelector("[data-editor-field='display_name']"), "display_name"
    ));
    let bio = root.querySelector(`.${PAGE}-bio`);
    if (!bio) {
      bio = document.createElement("p");
      bio.className = `${PAGE}-bio`;
      name.after(bio);
    }
    bio.replaceChildren(this.inlineField(
      this.editor.element.querySelector("[data-editor-field='bio']"), "bio"
    ));
    this.settingsButton = this.button(this.labels.settings, "edit", () => this.openSettings());
    this.settingsButton.classList.add("lib-editor__mobile-edit");
    this.settingsButton.setAttribute("aria-haspopup", "dialog");
    root.querySelector(`.${PAGE}-hero`).append(this.settingsButton);
    const socials = profile.querySelector(".c-link_in_bio--social_links-links") || document.createElement("div");
    socials.className = "c-link_in_bio--social_links-links";
    socials.replaceChildren();
    const controls = this.editor.element.querySelector("[data-controller~='link-in-bio-editor-social-controls']");
    controls.querySelectorAll("[data-link-in-bio-editor-social-controls-target~='configuredButton']:not([hidden])").forEach(source => {
      const button = this.button(source.getAttribute("aria-label"), "", () => this.activate(source));
      button.append(source.querySelector("svg").cloneNode(true));
      socials.append(button);
    });
    const addSocial = controls.querySelector("[data-action*='#openPicker']");
    if (controls.querySelector("[data-link-in-bio-editor-social-controls-target~='availableButton']:not([hidden])")) {
      socials.append(this.button(addSocial.getAttribute("aria-label"), "plus", () => this.activate(addSocial)));
    }
    profile.append(socials);
    this.editor.element.querySelectorAll("[data-editor-section]").forEach(section => {
      let preview = [...root.querySelectorAll("[data-link-in-bio-preview-section-id]")].find(node => node.dataset.linkInBioPreviewSectionId === section.dataset.clientKey);
      if (!preview) {
        preview = document.createElement("section");
        preview.className = `${PAGE}-section`;
        preview.dataset.linkInBioPreviewSectionId = section.dataset.clientKey;
      }
      // Include unnamed, provisional categories in the same order as the editor.
      root.querySelector(`.${PAGE}-content`).append(preview);
      const nameField = section.querySelector("[data-editor-field='name']");
      const heading = preview.querySelector("h2") || document.createElement("h2");
      const inlineName = this.inlineField(nameField, "section_name");
      heading.className = `${PAGE}-section-title`;
      heading.replaceChildren(
        inlineName,
        this.layoutControls(section),
        this.button(this.labels.options, "more", () => this.openSection(section))
      );
      preview.prepend(heading);
      this.editor.resizeCategoryName(inlineName.querySelector("[data-mobile-inline-field='section_name']"));
      preview.querySelectorAll("[data-link-in-bio-preview-item-id]").forEach(link => {
        const item = this.editor.itemsInSection(section).find(node => node.dataset.clientKey === link.dataset.linkInBioPreviewItemId);
        const button = this.button(item.querySelector("[data-editor-item-title]").textContent, "", () => this.openItem(item));
        button.className = link.className;
        button.dataset.mobileFocus = `item-${item.dataset.clientKey}`;
        button.append(...link.childNodes);
        link.replaceWith(button);
      });
      const add = this.button(this.labels.addLink, "plus", () => this.activate(section.querySelector("[data-add-item]")));
      add.append(document.createTextNode(this.labels.addLink));
      add.classList.add("lib-editor__mobile-add");
      preview.append(add);
    });
    const add = this.button(this.labels.addCategory, "plus", () => {
      const section = this.editor.createSection({ currentTarget: add }, { provisional: true });
      if (!section) return;
      this.pendingSection = section;
      section.querySelector("[data-editor-field='name']")?.addEventListener("input", () => {
        this.editor.commitProvisionalSection(section);
        this.pendingSection = null;
      }, { once: true });
      requestAnimationFrame(() => {
        this.editor.previewRoot
          ?.querySelector(`[data-link-in-bio-preview-section-id='${CSS.escape(section.dataset.clientKey)}'] [data-mobile-inline-field='section_name']`)
          ?.focus();
      });
    });
    add.append(document.createTextNode(this.labels.addCategory));
    add.classList.add("lib-editor__mobile-add");
    root.querySelector(`.${PAGE}-content`).append(add);
  }

  discardPendingSection() {
    if (!this.pendingSection) return;
    this.editor.discardProvisionalSection(this.pendingSection);
    this.pendingSection = null;
  }

  togglePreview() {
    this.previewing = !this.previewing;
    this.editor.refreshPreview();
  }

  action(source) {
    return {
      label: source.textContent.trim(), icon: source.querySelector("svg"), disabled: source.disabled,
      danger: source.dataset.sectionAction === "delete" || source.dataset.itemAction === "delete",
      run: async () => { await this.sheet.close(); source.click(); },
    };
  }

  layoutControls(section) {
    const controls = document.createElement("span");
    controls.className = "lib-editor__mobile-layout";
    section.querySelectorAll("[data-editor-field='layout']").forEach(source => {
      const label = source.labels[0];
      const button = this.button(label.textContent.trim(), "", () => {
        source.checked = true;
        source.dispatchEvent(new Event("change", { bubbles: true }));
      });
      button.setAttribute("aria-pressed", String(source.checked));
      button.append(label.querySelector("svg").cloneNode(true));
      controls.append(button);
    });
    return controls;
  }

  openSection(section) {
    const actions = [...section.querySelectorAll("[data-section-action]")].map(source => this.action(source));
    this.sheet.open({
      title: this.labels.options,
      owner: section,
      actions,
      showApply: false,
    });
  }

  openItem(item) {
    const editForm = item.querySelector("[data-link-in-bio-editor-item-target~='editForm']");
    const edit = item.querySelector("[data-item-action='edit']");
    if (editForm) this.editor.beginItemEdit({ currentTarget: edit });
    const actions = [...item.querySelectorAll("[data-item-action]")].filter(source => source.dataset.itemAction !== "edit").map(source => this.action(source));
    this.sheet.open({
      title: item.querySelector("[data-editor-item-title]").textContent,
      owner: item, content: editForm ? [editForm] : [], actions,
      apply: () => {
        if (!editForm) return;
        if (!this.editor.validateExternalInputs(item.querySelector("[data-editor-item-field='external_title']"), item.querySelector("[data-editor-item-field='external_url']"))) return false;
        this.editor.closeItemEdit({ currentTarget: editForm });
      },
      cancel: () => { if (editForm) this.editor.discardItemEdit({ currentTarget: editForm }); },
    });
  }

  openSettings() {
    const theme = this.editor.element.querySelector("[data-editor-field='theme']")?.closest("fieldset");
    const photo = this.editor.element.querySelector(".lib-editor__profile-picture-column");
    const slugField = this.editor.element.querySelector(".lib-editor__url-field");
    const groups = [];
    if (theme) groups.push({
      className: "lib-editor__mobile-settings-group--theme",
      content: [ theme ],
    });
    if (photo) groups.push({
      className: "lib-editor__mobile-settings-group--photo",
      content: [ photo ],
    });
    if (slugField) groups.push({
      className: "lib-editor__mobile-settings-group--url",
      content: [ slugField ],
    });

    this.sheet.open({
      title: this.labels.settings,
      owner: this.editor.element,
      groups,
      returnFocus: this.settingsButton,
      showApply: false,
      cancel: () => this.resetSettingsSlugEditor(),
    });
  }

  openSlugEditor(returnFocus) {
    if (!this.sheet?.element.open || this.detailSheet?.element.open) return false;
    const groupContent = this.sheet.bodyTarget.querySelector(".lib-editor__mobile-settings-group--url > .lib-editor__mobile-settings-group-content");
    if (!groupContent) return false;

    const title = groupContent.querySelector(".lib-editor__url-heading")?.textContent.trim() || "URL";
    this.detailSheet.open({
      title,
      owner: this.editor.element,
      content: [ groupContent ],
      returnFocus,
      showApply: false,
      cancel: () => this.resetSettingsSlugEditor({ restoreFocus: true }),
      closeAll: () => this.sheet.cancel(),
    });
    return true;
  }

  resetSettingsSlugEditor({ restoreFocus = false } = {}) {
    if (!this.editor.hasSlugEditorTarget || this.editor.slugEditorTarget.hidden) return;
    this.editor.slugInputTarget.value = this.editor.savedSlug;
    this.editor.finishSlugEdit({ restoreFocus });
  }

  get slugEditorOpen() {
    return Boolean(this.detailSheet?.element.open && this.detailSheet.bodyTarget.contains(this.editor.slugDisplayTarget.closest(".lib-editor__url-field")));
  }

  closeSlugEditor() {
    return this.detailSheet.close();
  }

}
